'use client'

import { useAuthorization } from '@entities/authentication'
import {
  SUBSCRIBE_TICKETS_TAB,
  subscribesTicketEntityQueries,
  SubscribeTicketCard,
  SubscribeTicketLanguageChip,
} from '@entities/subscribes'
import { LangType, LessonTime, lessonTimeSchema, promotionTypeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { FEATURE_FLAG_KEYS } from '@shared/constants'
import { useSuspenseQueries } from '@tanstack/react-query'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import z from 'zod'

import { Box, Flex, HStack, Typography, VStack } from '@podo-app/design-system-temp'
import { useFlagsCompat } from '@podo/flags/react'

import { LECTURE_TICKETS_QUERY_PARAMS, PAYBACK_BANNER_IMAGE_URL, parsePaybackFlag } from '../../model'

type Props = {
  langType: LangType
  lessonTime: LessonTime
}

export const PaybackLectureTickets = ({ langType, lessonTime }: Props) => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const searchParams = useSearchParams()
  const router = useRouter()
  const promotionType = promotionTypeSchema.Enum.PAYBACK

  const { payback_tickets: paybackFlag } = useFlagsCompat([FEATURE_FLAG_KEYS.PAYBACK_TICKETS])
  const content = useMemo(() => parsePaybackFlag(paybackFlag.value).content ?? {}, [paybackFlag.value])
  const defaultHeroChips = useMemo(
    () => [
      t('subscribeList.paymentTypeHero.paybackChipRefund'),
      t('subscribeList.paymentTypeHero.paybackChipFreeApplication'),
      t('subscribeList.paymentTypeHero.paybackChipEventPackage'),
    ],
    [t],
  )
  const fallbackCardDescriptions = useMemo(
    () => [
      t('subscribeList.ticketDescriptions.paybackMissionLessons'),
      t('subscribeList.ticketDescriptions.paybackMissionReview'),
      t('subscribeList.ticketDescriptions.paybackLimitedEvent'),
    ],
    [t],
  )

  const lessonTimeStr = lessonTime.toString() as NumberEnumToStringEnum<z.infer<typeof lessonTimeSchema>>

  const [{ data: lumpSumData }, { data: subscribeData }] = useSuspenseQueries({
    queries: [
      subscribesTicketEntityQueries.lumpSumList({
        bearerToken,
        langType,
        lessonTime: lessonTimeStr,
        promotionType,
      }),
      subscribesTicketEntityQueries.subscribeList({
        bearerToken,
        langType,
        lessonTime: lessonTimeStr,
        promotionType,
      }),
    ],
  })

  const subscribeTickets = [
    ...(Array.isArray(lumpSumData) ? lumpSumData : []),
    ...(Array.isArray(subscribeData) ? subscribeData : []),
  ]

  const buildQuery = (updater: (queryParams: URLSearchParams) => void) => {
    const queryParams = new URLSearchParams(searchParams.toString())
    queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.paymentType, SUBSCRIBE_TICKETS_TAB.LUMP_SUM)
    queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.promotionType, promotionType)
    updater(queryParams)
    router.push(`/subscribes/tickets/payback?${queryParams.toString()}`, {
      scroll: false,
    })
  }

  const heroTitle = content.hero?.title ?? t('subscribeList.paymentTypeHero.paybackTitle')
  const heroSubtitle =
    content.hero?.subtitle ??
    `${t('subscribeList.paymentTypeHero.paybackLine1')}\n${t('subscribeList.paymentTypeHero.paybackLine2')}`
  const heroChips = content.hero?.chips ?? defaultHeroChips
  const bannerAlt = t('subscribeList.paymentTypeHero.paybackBannerAlt')

  return (
    <VStack className="gap-8">
      <VStack className="gap-5">
        <VStack className="gap-2">
          <Typography size="h1" className="text-[28px] font-bold">
            {heroTitle}
          </Typography>
          <Typography size="h4" className="text-[18px]">
            {heroSubtitle.split('\n').map((line, index, arr) => (
              <span key={index}>
                {line}
                {index < arr.length - 1 && <br />}
              </span>
            ))}
          </Typography>
        </VStack>

        {heroChips.length > 0 && (
          <HStack className="gap-2.5 flex-wrap">
            {heroChips.map((chip) => (
              <Box key={chip} className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
                <Typography size="body1">{chip}</Typography>
              </Box>
            ))}
          </HStack>
        )}

        <Flex className="relative w-full h-[335px]">
          <Image
            src={PAYBACK_BANNER_IMAGE_URL}
            alt={bannerAlt}
            fill
            sizes="(max-width: 768px) 540px, 100vw"
            className="object-contain"
          />
        </Flex>
      </VStack>
      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.language')}</Typography>

        <SubscribeTicketLanguageChip
          selectedLanguage={langType}
          handleChangeLanguage={(language) =>
            buildQuery((queryParams) => {
              queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.langType, language)
            })
          }
          excludeLanguages={['JP']}
        />
      </VStack>

      {/* NOTE: 레슨 시간 추가 시 활성화 */}
      {/* <VStack className="gap-4">
        <Typography size="h3">레슨 시간을 선택하세요.</Typography>

        <SubscribeTicketLessonTimeChip
          selectedLessonTime={transformNumberEnumToStringEnum(lessonTime)}
          handleChangeLessonTime={(selectedLessonTime) =>
            buildQuery((queryParams) => {
              queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonTime, selectedLessonTime)
            })
          }
        />
      </VStack> */}

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.product')}</Typography>

        {subscribeTickets.map((subscribeTicket) => (
          <SubscribeTicketCard
            month={subscribeTicket.lessonMonth}
            key={subscribeTicket.id}
            subscribeTicketId={subscribeTicket.id}
            catalogTicket={subscribeTicket}
            price={subscribeTicket.originPrice}
            descriptions={
              content.cardDescriptionsByMonth?.[String(subscribeTicket.lessonMonth)] ?? fallbackCardDescriptions
            }
            isBest={subscribeTicket.isBest}
          />
        ))}
      </VStack>
    </VStack>
  )
}
